import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '@/lib/api'

export interface User {
  id: string
  email: string
  full_name: string
  role: string
  role_level?: number
  holding_id: string | null
  empresa_id?: string
  avatar_url?: string
  language: string
  mfa_enabled: boolean
}

// INC-031: Multi-holding support
export interface HoldingInfo {
  id: string
  code: string
  name: string
  is_default: boolean
}

// SSO Provider info
export interface SSOProviderInfo {
  id: string
  name: string
  display_name: string
  icon: string
  color: string
  enabled: boolean
}

// RBAC: Role info with scope
export interface RoleInfo {
  code: string
  name: string
  scope_type: string
}

// ALTA-014: Tenant Bootstrap data
export interface TenantBootstrap {
  tenantSlug: string | null
  holdingId: string | null
  holdingName: string | null
  branding: {
    logoUrl: string | null
    primaryColor: string
    secondaryColor: string
    faviconUrl: string | null
  }
  authPolicy: {
    allowPasswordLogin: boolean
    mfaRequired: boolean
    defaultMethod: string
    availableSSOProviders: Array<{
      providerType: string
      displayName: string
      icon: string | null
    }>
  }
  error: string | null
}

// Map role_level to role string
const ROLE_MAP: Record<number, string> = {
  1: 'gam_admin',
  2: 'admin_cp',
  3: 'admin_holding',
  4: 'admin_empresa',
  5: 'admin_area',
  6: 'usuario_avanzado',
  7: 'usuario_basico',
}

function mapUserFromBackend(backendUser: Record<string, unknown> | null | undefined): User | null {
  if (!backendUser) return null
  const roleLevel = (backendUser.role_level as number) || 7
  // Handle both login response (id) and /me response (user_id)
  const id = (backendUser.id || backendUser.user_id) as string
  // Handle both login response (first_name/last_name) and /me response (email only)
  const firstName = backendUser.first_name as string || ''
  const lastName = backendUser.last_name as string || ''
  const fullName = `${firstName} ${lastName}`.trim() || backendUser.email as string
  // Language: read from backend, fallback to localStorage i18nextLng, then default to 'es'
  const storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null
  const language = (backendUser.language as string) || storedLang || 'es'
  return {
    id,
    email: backendUser.email as string,
    full_name: fullName,
    role: ROLE_MAP[roleLevel] || 'usuario_basico',
    role_level: roleLevel,
    holding_id: (backendUser.holding_id as string) ?? null,
    empresa_id: backendUser.empresa_id as string | undefined,
    language,
    mfa_enabled: (backendUser.mfa_enabled as boolean) || false,
  }
}

interface LoginResult {
  requiresMFA: boolean
  requiresHoldingSelection: boolean
  tempToken?: string
  holdings?: HoldingInfo[]
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  // INC-031: Multi-holding support
  holdings: HoldingInfo[]
  activeHoldingId: string | null
  requiresHoldingSelection: boolean
  tempToken: string | null
  // SSO support
  ssoProviders: SSOProviderInfo[]
  traditionalLoginEnabled: boolean
  ssoLoading: string | null  // provider ID being loaded
  // ALTA-014: Tenant Bootstrap
  tenantBootstrap: TenantBootstrap | null
  bootstrapLoading: boolean
  bootstrapError: string | null
  // RBAC: Permissions, roles, and feature flags
  permissions: string[]
  roles: RoleInfo[]
  features: string[]
  // INC-077: Products available for current holding
  products: string[]

  // Actions
  login: (email: string, password: string, holdingId?: string) => Promise<LoginResult>
  verifyMFA: (code: string, tempToken: string) => Promise<void>
  register: (email: string, password: string, fullName: string) => Promise<void>
  logout: () => void
  loadUser: () => Promise<void>
  loadPermissions: () => Promise<void>
  setError: (error: string | null) => void
  // INC-031: Holding selection actions
  selectHolding: (holdingId: string) => Promise<void>
  switchHolding: (holdingId: string) => Promise<void>
  // SSO actions
  loadSSOProviders: (holdingId?: string) => Promise<void>
  initiateSSOLogin: (providerId: string, holdingId?: string) => Promise<void>
  handleSSOCallback: (providerId: string, code: string, state: string) => Promise<LoginResult>
  // ALTA-014: Tenant Bootstrap actions
  loadTenantBootstrap: (overrideHost?: string) => Promise<TenantBootstrap | null>
  // ALTA-014: Handle SSO complete redirect from centralized callback
  handleSSOCompleteRedirect: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      // INC-031: Multi-holding support
      holdings: [],
      activeHoldingId: null,
      requiresHoldingSelection: false,
      tempToken: null,
      // SSO support
      ssoProviders: [],
      traditionalLoginEnabled: true,
      ssoLoading: null,
      // ALTA-014: Tenant Bootstrap
      tenantBootstrap: null,
      bootstrapLoading: false,
      bootstrapError: null,
      // RBAC: Permissions, roles, and feature flags
      permissions: [],
      roles: [],
      features: [],
      // INC-077: Products available for current holding
      products: [],

      login: async (email: string, password: string, holdingId?: string) => {
        set({ isLoading: true, error: null })
        try {
          // ALTA-014: Pass holdingId from tenant bootstrap to enforce auth policies
          const response = await authApi.login(email, password, holdingId)
          const data = response.data

          // Handle MFA requirement
          if (data.mfa_required) {
            set({ isLoading: false })
            return { requiresMFA: true, requiresHoldingSelection: false, tempToken: data.temp_token }
          }

          // INC-031: Handle multi-holding selection
          const holdings = (data.holdings || []) as HoldingInfo[]

          if (data.requires_holding_selection) {
            set({
              isLoading: false,
              holdings,
              requiresHoldingSelection: true,
              tempToken: data.temp_token,
            })
            return { requiresMFA: false, requiresHoldingSelection: true, tempToken: data.temp_token, holdings }
          }

          // Normal login with automatic holding selection
          localStorage.setItem('token', data.access_token)
          const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
          if (activeHoldingId) {
            localStorage.setItem('holdingId', activeHoldingId)
          }
          if (data.user?.empresa_id) {
            localStorage.setItem('empresaId', data.user.empresa_id)
          }

          const mappedUser = mapUserFromBackend(data.user)

          set({
            user: mappedUser,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings,
            activeHoldingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })

          return { requiresMFA: false, requiresHoldingSelection: false }
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      verifyMFA: async (code: string, tempToken: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.verifyMFA(code, tempToken)
          const data = response.data

          localStorage.setItem('token', data.access_token)
          if (data.user?.holding_id) {
            localStorage.setItem('holdingId', data.user.holding_id)
          }

          set({
            user: mapUserFromBackend(data.user),
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'MFA verification failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      register: async (email: string, password: string, fullName: string) => {
        set({ isLoading: true, error: null })
        try {
          await authApi.register({ email, password, full_name: fullName })
          set({ isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      logout: () => {
        localStorage.removeItem('token')
        localStorage.removeItem('holdingId')
        localStorage.removeItem('empresaId')
        localStorage.removeItem('sso_state')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          // INC-031: Clear multi-holding state
          holdings: [],
          activeHoldingId: null,
          requiresHoldingSelection: false,
          tempToken: null,
          // Clear SSO state
          ssoLoading: null,
          // RBAC: Clear permissions state
          permissions: [],
          roles: [],
          features: [],
          // INC-077: Clear products
          products: [],
        })
      },

      loadUser: async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          set({ isLoading: false })
          return
        }

        try {
          const response = await authApi.me()
          const mappedUser = mapUserFromBackend(response.data)

          // Also update localStorage with holding and empresa IDs
          if (mappedUser?.holding_id) {
            localStorage.setItem('holdingId', mappedUser.holding_id)
          }
          if (mappedUser?.empresa_id) {
            localStorage.setItem('empresaId', mappedUser.empresa_id)
          }

          set({
            user: mappedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
          })

          // RBAC: Load permissions after user is loaded
          await get().loadPermissions()
        } catch {
          get().logout()
        }
      },

      // RBAC: Load user permissions, roles, feature flags, and products
      loadPermissions: async () => {
        try {
          // Load permissions
          const response = await authApi.myPermissions()
          const data = response.data

          // Map roles to RoleInfo format
          const roles: RoleInfo[] = (data.roles || []).map((r: { code: string; name: string; scope_type?: string }) => ({
            code: r.code,
            name: r.name,
            scope_type: r.scope_type || 'global',
          }))

          set({
            permissions: data.permissions || [],
            roles,
            features: data.features || [],
          })

          // INC-077: Load products for current holding
          try {
            const productsResponse = await authApi.myProducts()
            const productsData = productsResponse.data
            set({
              products: productsData.enabled_codes || [],
            })
          } catch (productsError) {
            console.error('Failed to load products:', productsError)
            // Don't fail - keep empty products
            set({ products: [] })
          }
        } catch (error) {
          console.error('Failed to load permissions:', error)
          // Don't fail silently - keep empty arrays
          set({
            permissions: [],
            roles: [],
            features: [],
            products: [],
          })
        }
      },

      setError: (error: string | null) => set({ error }),

      // INC-031: Select holding after login (when requires_holding_selection=true)
      selectHolding: async (holdingId: string) => {
        const { tempToken, holdings } = get()
        if (!tempToken) {
          throw new Error('No temporary token available')
        }

        set({ isLoading: true, error: null })
        try {
          const response = await authApi.selectHolding(holdingId, tempToken)
          const data = response.data

          localStorage.setItem('token', data.access_token)
          localStorage.setItem('holdingId', holdingId)

          const mappedUser = mapUserFromBackend(data.user)
          const updatedHoldings = (data.holdings || holdings) as HoldingInfo[]

          set({
            user: mappedUser,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings: updatedHoldings,
            activeHoldingId: holdingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to select holding'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // INC-031: Switch to a different holding while logged in
      switchHolding: async (holdingId: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.switchHolding(holdingId)
          const data = response.data

          localStorage.setItem('token', data.access_token)
          localStorage.setItem('holdingId', holdingId)

          const mappedUser = mapUserFromBackend(data.user)
          const updatedHoldings = (data.holdings || get().holdings) as HoldingInfo[]

          set({
            user: mappedUser,
            token: data.access_token,
            isLoading: false,
            holdings: updatedHoldings,
            activeHoldingId: holdingId,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to switch holding'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // SSO: Load available providers for a holding
      loadSSOProviders: async (holdingId?: string) => {
        try {
          const response = await authApi.getSSOProviders(holdingId)
          const data = response.data
          set({
            ssoProviders: data.providers as SSOProviderInfo[],
            traditionalLoginEnabled: data.traditional_login_enabled,
          })
        } catch (error: unknown) {
          console.error('Failed to load SSO providers:', error)
          // Don't set error - fall back to traditional login
          set({
            ssoProviders: [],
            traditionalLoginEnabled: true,
          })
        }
      },

      // SSO: Initiate login with an SSO provider
      initiateSSOLogin: async (providerId: string, holdingId?: string) => {
        set({ ssoLoading: providerId, error: null })
        try {
          const response = await authApi.initiateSSOLogin(providerId, holdingId)
          const { auth_url, state } = response.data

          // Store state for CSRF validation on callback
          localStorage.setItem('sso_state', state)
          if (holdingId) {
            localStorage.setItem('sso_holding_id', holdingId)
          }

          // Redirect to IdP
          window.location.href = auth_url
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to initiate SSO login'
          set({ error: message, ssoLoading: null })
          throw error
        }
      },

      // SSO: Handle callback from IdP
      // ALTA-014: Supports centralized redirect - if redirect_host differs from current host,
      // redirects to original tenant with tokens passed via URL hash
      handleSSOCallback: async (providerId: string, code: string, state: string) => {
        set({ isLoading: true, error: null })
        try {
          // Validate state
          const storedState = localStorage.getItem('sso_state')
          if (storedState && storedState !== state) {
            throw new Error('Invalid SSO state - possible CSRF attack')
          }

          const response = await authApi.handleSSOCallback(providerId, code, state)
          const data = response.data

          // Clean up SSO state
          localStorage.removeItem('sso_state')
          localStorage.removeItem('sso_holding_id')

          // ALTA-014: Check if we need to redirect to original tenant subdomain
          const currentHost = window.location.host
          const redirectHost = data.redirect_host

          if (redirectHost && redirectHost !== currentHost && !data.mfa_required && !data.requires_holding_selection) {
            // Aikido HIGH: validate redirectHost against tenant pattern to prevent
            // open redirect / token-leak via malicious backend response.
            // Allowed: <tenant>.app.aldo.bupry.com, app.aldo.bupry.com, localhost(:port)
            const HOST_ALLOWLIST = /^(localhost(:\d+)?|[a-z0-9-]+\.aldo\.bupry\.com)$/i
            if (!HOST_ALLOWLIST.test(redirectHost)) {
              throw new Error('Invalid SSO redirect host')
            }
            // Need to redirect to original tenant with tokens
            // Use URL hash to pass tokens securely (hash is not sent to server)
            const ssoResult = {
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              user: data.user,
              holdings: data.holdings || [],
            }
            const encodedResult = btoa(JSON.stringify(ssoResult))
            // Redirect to original tenant with SSO result in hash
            window.location.href = `https://${redirectHost}/#sso_complete=${encodedResult}`
            // Return early - the redirect will navigate away
            return { requiresMFA: false, requiresHoldingSelection: false }
          }

          // Handle MFA requirement (if IdP user has MFA enabled)
          if (data.mfa_required) {
            set({ isLoading: false })
            return { requiresMFA: true, requiresHoldingSelection: false, tempToken: data.temp_token }
          }

          // Handle multi-holding selection
          const holdings = (data.holdings || []) as HoldingInfo[]

          if (data.requires_holding_selection) {
            set({
              isLoading: false,
              holdings,
              requiresHoldingSelection: true,
              tempToken: data.temp_token,
            })
            return { requiresMFA: false, requiresHoldingSelection: true, tempToken: data.temp_token, holdings }
          }

          // Normal login completion (same host or no redirect needed)
          localStorage.setItem('token', data.access_token)
          const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
          if (activeHoldingId) {
            localStorage.setItem('holdingId', activeHoldingId)
          }
          if (data.user?.empresa_id) {
            localStorage.setItem('empresaId', data.user.empresa_id)
          }

          const mappedUser = mapUserFromBackend(data.user)

          set({
            user: mappedUser,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings,
            activeHoldingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })

          return { requiresMFA: false, requiresHoldingSelection: false }
        } catch (error: unknown) {
          // Clean up SSO state on error
          localStorage.removeItem('sso_state')
          localStorage.removeItem('sso_holding_id')
          const message = error instanceof Error ? error.message : 'SSO authentication failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // ALTA-014: Load tenant bootstrap from hostname
      loadTenantBootstrap: async (overrideHost?: string) => {
        set({ bootstrapLoading: true, bootstrapError: null })
        try {
          // ALTA-014: Always pass current hostname so backend can resolve tenant
          const hostToUse = overrideHost || window.location.host
          const response = await authApi.tenantBootstrap(hostToUse)
          const data = response.data

          // Check for bootstrap error
          if (data.error) {
            set({
              bootstrapLoading: false,
              bootstrapError: data.error,
              tenantBootstrap: data,
              // If there's an error but authPolicy exists, use it
              traditionalLoginEnabled: data.authPolicy?.allowPasswordLogin ?? true,
            })
            return data
          }

          // Convert SSO providers from bootstrap to store format
          const ssoProviders: SSOProviderInfo[] = data.authPolicy.availableSSOProviders.map((p) => ({
            id: p.providerType,
            name: p.displayName,
            display_name: p.displayName,
            icon: p.icon || 'key',
            color: '#6b7280',
            enabled: true,
          }))

          set({
            bootstrapLoading: false,
            bootstrapError: null,
            tenantBootstrap: data,
            // Update SSO state from bootstrap
            ssoProviders,
            traditionalLoginEnabled: data.authPolicy.allowPasswordLogin,
          })

          return data
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to load tenant configuration'
          set({
            bootstrapLoading: false,
            bootstrapError: message,
            tenantBootstrap: null,
            // Fallback to traditional login on error
            traditionalLoginEnabled: true,
            ssoProviders: [],
          })
          return null
        }
      },

      // ALTA-014: Handle SSO complete redirect from centralized callback
      // Called on app initialization to check for sso_complete hash
      handleSSOCompleteRedirect: () => {
        const hash = window.location.hash
        if (!hash.includes('sso_complete=')) {
          return false
        }

        try {
          // Extract and decode the SSO result
          const match = hash.match(/sso_complete=([^&]+)/)
          if (!match) {
            return false
          }

          const encodedResult = match[1]
          const ssoResult = JSON.parse(atob(encodedResult))

          // Store tokens
          localStorage.setItem('token', ssoResult.access_token)
          const holdings = (ssoResult.holdings || []) as HoldingInfo[]
          const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
          if (activeHoldingId) {
            localStorage.setItem('holdingId', activeHoldingId)
          }
          if (ssoResult.user?.empresa_id) {
            localStorage.setItem('empresaId', ssoResult.user.empresa_id)
          }

          // Map user
          const mappedUser = mapUserFromBackend(ssoResult.user)

          // Update store
          set({
            user: mappedUser,
            token: ssoResult.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings,
            activeHoldingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })

          // Clear the hash from URL (without triggering a reload)
          window.history.replaceState(null, '', window.location.pathname + window.location.search)

          return true
        } catch (error) {
          console.error('Failed to process SSO complete redirect:', error)
          // Clear invalid hash
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
          return false
        }
      },
    }),
    {
      name: 'auth-storage',
      // Persist token and user for session restoration
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // INC-031: Persist holdings state
        holdings: state.holdings,
        activeHoldingId: state.activeHoldingId,
        // RBAC: Persist permissions state
        permissions: state.permissions,
        roles: state.roles,
        features: state.features,
        // INC-077: Persist products
        products: state.products,
      }),
    }
  )
)
